/**
 * Tawk.to live chat loader (#152).
 *
 * Env-driven like the API base: with REACT_APP_TAWK_PROPERTY_ID unset the
 * widget never loads, so an unconfigured build simply has no chat.
 */

const PROPERTY_ID = process.env.REACT_APP_TAWK_PROPERTY_ID;
const WIDGET_ID = process.env.REACT_APP_TAWK_WIDGET_ID || 'default';
const SCRIPT_ID = 'tawk-to-embed';
/** Which account the widget is currently identified as. */
const IDENTITY_KEY = 'wsd_tawk_identity';

let scriptInjected = false;

export function isTawkConfigured() {
	return Boolean(PROPERTY_ID);
}

/** Visitor attributes for the signed-in account; empty for an anonymous chat. */
export function tawkVisitor(user) {
	if (!user) return {};
	const name = [user.given_name, user.family_name].filter(Boolean).join(' ').trim();
	const visitor = {};
	if (name) visitor.name = name;
	if (user.email) visitor.email = user.email;
	if (user.phone_number) visitor.phone = user.phone_number;
	return visitor;
}

export function tawkIdentity(visitor) {
	return [visitor?.phone || '', visitor?.email || '', visitor?.name || ''].join('|');
}

/**
 * Load the widget and identify the signed-in account, ending the previous
 * Tawk visitor session first when the account has changed.
 *
 * Tawk remembers its own visitor in browser storage and prefers it over
 * `Tawk_API.visitor`, so without the reset the next account to open chat
 * inherits the previous one's name and transcript (#152).
 */
export function syncTawk(visitor) {
	if (!isTawkConfigured() || typeof window === 'undefined') return;

	window.Tawk_API = window.Tawk_API || {};
	applyVisitorFields(visitor);

	if (!scriptInjected) {
		scriptInjected = true;
		window.Tawk_API.onLoad = () => reconcile(visitor);
		injectScript();
		return;
	}
	reconcile(visitor);
}

function applyVisitorFields(visitor) {
	// Read as the script loads, so it has to be set before the tag is appended.
	if (visitor?.name || visitor?.email) {
		window.Tawk_API.visitor = { name: visitor.name || '', email: visitor.email || '' };
	}
}

function injectScript() {
	if (document.getElementById(SCRIPT_ID)) return;
	window.Tawk_LoadStart = new Date();
	const script = document.createElement('script');
	script.id = SCRIPT_ID;
	script.async = true;
	script.src = `https://embed.tawk.to/${PROPERTY_ID}/${WIDGET_ID}`;
	script.charset = 'UTF-8';
	script.setAttribute('crossorigin', '*');
	document.head.appendChild(script);
}

function reconcile(visitor) {
	const identity = tawkIdentity(visitor);
	const previous = readIdentity();
	// No stored identity but Tawk already holds a visitor: a browser that
	// chatted before this tracking existed, so treat it as a change too.
	const changed = previous === null ? hasTawkState() : previous !== identity;

	if (changed) {
		// Write first: the reload below must see a matching identity, or it
		// would reset and reload again on every load.
		writeIdentity(identity);
		resetVisitorSession();
		return;
	}
	identify(visitor, identity);
}

function identify(visitor, identity) {
	applyVisitorFields(visitor);
	setTawkVisitor(visitor);
	writeIdentity(identity);
}

/** Tawk's own visitor state: first-party cookies plus localStorage. */
function tawkStateKeys() {
	let cookies = [];
	let stored = [];
	try {
		cookies = document.cookie
			.split(';')
			.map((entry) => entry.trim().split('=')[0])
			.filter((name) => name && (name.startsWith('twk') || name === 'TawkConnectionTime'));
	} catch (error) {
		/* cookies unavailable */
	}
	try {
		stored = Object.keys(window.localStorage).filter((key) => key.startsWith('twk_'));
	} catch (error) {
		/* storage unavailable */
	}
	return { cookies, stored };
}

function hasTawkState() {
	const { cookies, stored } = tawkStateKeys();
	return cookies.length > 0 || stored.length > 0;
}

/**
 * Drop Tawk's remembered visitor so its chat history doesn't carry over to the
 * next account. `Tawk_API.logout()` does not do this — the identity lives in
 * first-party `twk*` cookies, and the widget also holds it in memory, so the
 * page has to come back up clean (#152).
 */
function resetVisitorSession() {
	const { cookies, stored } = tawkStateKeys();
	cookies.forEach((name) => {
		document.cookie = `${name}=; Max-Age=0; path=/`;
		document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
	});
	try {
		stored.forEach((key) => window.localStorage.removeItem(key));
	} catch (error) {
		/* storage unavailable */
	}
	window.location.reload();
}

/** Push the account onto an already-loaded widget (login after first paint). */
export function setTawkVisitor(visitor) {
	const api = typeof window !== 'undefined' ? window.Tawk_API : null;
	if (!api || typeof api.setAttributes !== 'function') return;
	// Throws when the widget has not finished loading — harmless, the pre-load
	// `visitor` above already covers that case.
	try {
		api.setAttributes(visitor, () => {});
	} catch (error) {
		/* widget not ready yet */
	}
}

function readIdentity() {
	try {
		return window.localStorage.getItem(IDENTITY_KEY);
	} catch (error) {
		return null;
	}
}

function writeIdentity(identity) {
	try {
		window.localStorage.setItem(IDENTITY_KEY, identity);
	} catch (error) {
		/* storage unavailable */
	}
}
